import React, { useState, useEffect } from 'react';
import { Input, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { projectsAPI } from '../../helpers/api';
import SitePersonnelModal from './SitePersonnelModal';
import SiteWorksModal from './SiteWorksModal';
import SiteDailyRecordsModal from './SiteDailyRecordsModal';
import SiteWaybillsModal from './SiteWaybillsModal';
import SiteVehiclesModal from './SiteVehiclesModal';

interface Site {
  id: string;
  name: string;
  code: string;
  address: string | null;
  status: string;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
  project: {
    id: string;
    name: string;
    code: string;
    city: { id: string; name: string; parentId?: string | null };
  };
  city?: { id: string; name: string; parentId?: string | null } | null;
}

interface City {
  id: string;
  name: string;
  code?: string;
}

interface District {
  id: string;
  name: string;
}

interface Location {
  id: string;
  name: string;
  neighborhood: string | null;
  street: string | null;
  buildingNo: string | null;
  description: string | null;
}

interface SiteDetailModalProps {
  isOpen: boolean;
  toggle: () => void;
  site: Site | null;
  onUpdate?: () => void;
}

const statusStyles: Record<string, string> = {
  ACTIVE: 'bg-success bg-opacity-10 text-success',
  COMPLETED: 'bg-info bg-opacity-10 text-info',
  SUSPENDED: 'bg-warning bg-opacity-10 text-warning',
};

const statusLabels: Record<string, string> = {
  ACTIVE: 'Aktif',
  COMPLETED: 'Tamamlandı',
  SUSPENDED: 'Askıda',
};

const statusOptions = [
  { value: 'ACTIVE', label: 'Aktif' },
  { value: 'COMPLETED', label: 'Tamamlandı' },
  { value: 'SUSPENDED', label: 'Askıda' },
];

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const SiteDetailModal: React.FC<SiteDetailModalProps> = ({
  isOpen,
  toggle,
  site,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    status: '',
    provinceId: '',
    districtId: '',
  });

  // Sub-modal states
  const [personnelModalOpen, setPersonnelModalOpen] = useState(false);
  const [vehiclesModalOpen, setVehiclesModalOpen] = useState(false);
  const [worksModalOpen, setWorksModalOpen] = useState(false);
  const [dailyRecordsModalOpen, setDailyRecordsModalOpen] = useState(false);
  const [waybillsModalOpen, setWaybillsModalOpen] = useState(false);

  // Locations state
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationNeighborhood, setNewLocationNeighborhood] = useState('');
  const [addingLocation, setAddingLocation] = useState(false);

  // Reset form when site changes
  useEffect(() => {
    if (site) {
      // Şantiyenin kendi ili varsa onu kullan, yoksa projenin ilini
      const siteCity = site.city;
      const provinceId = siteCity?.parentId || siteCity?.id || site.project.city.parentId || site.project.city.id || '';

      setFormData({
        name: site.name || '',
        code: site.code || '',
        address: site.address || '',
        status: site.status || '',
        provinceId,
        districtId: siteCity?.parentId ? (siteCity?.id || '') : '',
      });
      setIsEditing(false);
      setShowAddLocation(false);
      fetchLocations(site.id);
      // İlleri ve ilçeleri yükle
      loadCities();
      if (provinceId) {
        loadDistricts(provinceId);
      }
    }
  }, [site]);

  const loadCities = async () => {
    try {
      const res = await projectsAPI.getCities();
      setCities(res.data);
    } catch (error) {
      console.error('İller yüklenemedi:', error);
    }
  };

  const loadDistricts = async (provinceId: string) => {
    if (!provinceId) {
      setDistricts([]);
      return;
    }
    try {
      setLoadingDistricts(true);
      const res = await projectsAPI.getDistricts(provinceId);
      setDistricts(res.data);
    } catch (error) {
      console.error('İlçeler yüklenemedi:', error);
    } finally {
      setLoadingDistricts(false);
    }
  };

  const handleProvinceChange = (provinceId: string) => {
    setFormData(prev => ({ ...prev, provinceId, districtId: '' }));
    setDistricts([]);
    if (provinceId) {
      loadDistricts(provinceId);
    }
  };

  const fetchLocations = async (siteId: string) => {
    try {
      setLoadingLocations(true);
      const res = await projectsAPI.getSiteLocations(siteId);
      setLocations(res.data);
    } catch (error) {
      console.error('Mahaller yüklenemedi:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleAddLocation = async () => {
    if (!site || !newLocationName.trim()) return;
    try {
      setAddingLocation(true);
      await projectsAPI.createLocation(site.id, {
        name: newLocationName.trim(),
        neighborhood: newLocationNeighborhood.trim() || undefined,
      });
      toast.success('Mahal eklendi');
      setNewLocationName('');
      setNewLocationNeighborhood('');
      setShowAddLocation(false);
      fetchLocations(site.id);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Mahal eklenemedi');
    } finally {
      setAddingLocation(false);
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!site) return;

    try {
      setSaving(true);
      await projectsAPI.updateSite(site.id, {
        name: formData.name,
        code: formData.code,
        address: formData.address || null,
        status: formData.status,
        cityId: formData.districtId || formData.provinceId || null,
      });
      toast.success('Şantiye güncellendi');
      setIsEditing(false);
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (site) {
      const siteCity = site.city;
      const provinceId = siteCity?.parentId || siteCity?.id || site.project.city.parentId || site.project.city.id || '';

      setFormData({
        name: site.name || '',
        code: site.code || '',
        address: site.address || '',
        status: site.status || '',
        provinceId,
        districtId: siteCity?.parentId ? (siteCity?.id || '') : '',
      });
      if (provinceId) {
        loadDistricts(provinceId);
      }
    }
    setIsEditing(false);
  };

  const handleDeleteSite = async () => {
    if (!site) return;
    if (!window.confirm('Bu şantiyeyi silmek istediğinizden emin misiniz?')) return;
    try {
      await projectsAPI.deleteSite(site.id);
      toast.success('Şantiye silindi');
      toggle();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Silme başarısız');
    }
  };

  if (!site) return null;

  const headerActions = (
    <>
      {!isEditing ? (
        <>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={() => setIsEditing(true)}
            title="Düzenle"
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleDeleteSite}
            title="Sil"
            style={{ color: '#f06548' }}
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleSave}
            disabled={saving}
            title="Kaydet"
            style={{ color: saving ? '#adb5bd' : '#0ab39c' }}
          >
            {saving ? <Spinner size="sm" /> : <i className="ri-check-line"></i>}
          </button>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleCancel}
            disabled={saving}
            title="İptal"
          >
            <i className="ri-close-line"></i>
          </button>
        </>
      )}
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Şantiye Detayı"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
      hideBackdrop
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header */}
        <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }}>
          <div className="d-flex align-items-start justify-content-between">
            <div style={{ flex: 1, marginRight: '1rem' }}>
              {isEditing ? (
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-control-lg mb-2"
                  style={{ fontWeight: 600 }}
                />
              ) : (
                <h4 className="mb-1" style={{ fontWeight: 600 }}>
                  {site.name}
                </h4>
              )}
              {isEditing ? (
                <Input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="Şantiye Kodu"
                  bsSize="sm"
                  style={{ width: '150px' }}
                />
              ) : (
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                  {site.code} • {site.project.name}
                </p>
              )}
            </div>
            {isEditing ? (
              <Input
                type="select"
                name="status"
                value={formData.status}
                onChange={handleSelectChange}
                bsSize="sm"
                style={{ width: 'auto' }}
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Input>
            ) : (
              <span
                className={`px-2 py-1 rounded-pill small ${statusStyles[site.status]}`}
                style={{ fontWeight: 500 }}
              >
                {statusLabels[site.status]}
              </span>
            )}
          </div>
        </div>

        {/* Details Grid */}
        <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }}>
          <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
            Detaylar
          </div>
          <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
            {/* Proje */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Proje</span>
              <span style={{ fontWeight: 500 }}>{site.project.name}</span>
            </div>
            {/* İl */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">İl</span>
              {isEditing ? (
                <Input
                  type="select"
                  value={formData.provinceId}
                  onChange={(e) => handleProvinceChange(e.target.value)}
                  bsSize="sm"
                  style={{ width: '180px' }}
                >
                  <option value="">İl Seçin</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.code ? `${city.code} - ${city.name}` : city.name}
                    </option>
                  ))}
                </Input>
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {(() => {
                    const c = site.city;
                    if (c?.parentId) {
                      const province = cities.find(p => p.id === c.parentId);
                      return province?.name || site.project.city.name;
                    }
                    return c?.name || site.project.city.name;
                  })()}
                </span>
              )}
            </div>
            {/* İlçe */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">İlçe</span>
              {isEditing ? (
                <Input
                  type="select"
                  name="districtId"
                  value={formData.districtId}
                  onChange={handleSelectChange}
                  bsSize="sm"
                  style={{ width: '180px' }}
                  disabled={!formData.provinceId || loadingDistricts}
                >
                  <option value="">
                    {loadingDistricts ? 'Yükleniyor...' : 'İlçe Seçin'}
                  </option>
                  {districts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name}
                    </option>
                  ))}
                </Input>
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {site.city?.parentId ? site.city.name : '-'}
                </span>
              )}
            </div>
            {/* Kayıt Tarihi */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2">
              <span className="text-muted">Kayıt Tarihi</span>
              <span>{formatDate(site.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }}>
          <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
            Adres
          </div>
          {isEditing ? (
            <Input
              type="textarea"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              rows={2}
              placeholder="Şantiye adresi..."
            />
          ) : (
            <p className="mb-0" style={{ color: site.address ? '#495057' : '#adb5bd' }}>
              {site.address || 'Adres belirtilmedi'}
            </p>
          )}
        </div>

        {/* Mahaller */}
        <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #0ab39c' }}>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-2"
                style={{ width: 28, height: 28, background: '#e6f7f2', color: '#0ab39c' }}
              >
                <i className="ri-map-pin-line" style={{ fontSize: '0.9rem' }}></i>
              </span>
              <span className="small" style={{ fontWeight: 600, color: '#344054' }}>
                Mahaller
                {locations.length > 0 && (
                  <span className="ms-1 text-muted" style={{ fontWeight: 400 }}>({locations.length})</span>
                )}
              </span>
            </div>
            {showAddLocation ? (
              <button
                type="button"
                className="btn btn-sm px-2 py-0"
                style={{ fontSize: '0.75rem', color: '#667085', border: '1px solid #e4e7ec', borderRadius: '6px', lineHeight: '24px' }}
                onClick={() => setShowAddLocation(false)}
              >
                Vazgec
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-sm px-2 py-0"
                style={{ fontSize: '0.75rem', color: '#0ab39c', background: '#e6f7f2', border: '1px solid #b8e6d8', borderRadius: '6px', lineHeight: '24px' }}
                onClick={() => setShowAddLocation(true)}
              >
                <i className="ri-add-circle-line me-1" style={{ fontSize: '0.7rem' }}></i>
                Mahal Ekle
              </button>
            )}
          </div>

          {showAddLocation && (
            <div className="rounded-3 p-3 mb-3" style={{ background: '#f9fafb', border: '1px dashed #d0d5dd' }}>
              <Input
                type="text"
                bsSize="sm"
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                placeholder="Mahal adı *"
                className="mb-2"
              />
              <Input
                type="text"
                bsSize="sm"
                value={newLocationNeighborhood}
                onChange={(e) => setNewLocationNeighborhood(e.target.value)}
                placeholder="Mahalle (opsiyonel)"
                className="mb-2"
              />
              <button
                type="button"
                className="btn btn-sm w-100"
                style={{ background: '#0ab39c', color: '#fff', borderRadius: '8px' }}
                onClick={handleAddLocation}
                disabled={!newLocationName.trim() || addingLocation}
              >
                {addingLocation ? 'Ekleniyor...' : 'Ekle'}
              </button>
            </div>
          )}

          {loadingLocations ? (
            <div className="text-center py-2"><Spinner size="sm" color="primary" /></div>
          ) : locations.length === 0 ? (
            <div className="text-center py-3 rounded-3" style={{ background: '#f9fafb' }}>
              <p className="text-muted mb-0 small">Henüz mahal eklenmemiş</p>
            </div>
          ) : (
            <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              {locations.map((loc, idx) => (
                <div
                  key={loc.id}
                  className="d-flex align-items-center gap-2 px-3 py-2"
                  style={{ borderBottom: idx < locations.length - 1 ? '1px solid #f2f4f7' : 'none' }}
                >
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-2"
                    style={{ width: 24, height: 24, background: '#e6f7f2', color: '#0ab39c', fontSize: '0.65rem', flexShrink: 0 }}
                  >
                    <i className="ri-map-pin-line"></i>
                  </span>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{loc.name}</div>
                    {loc.neighborhood && (
                      <small className="text-muted">{loc.neighborhood}</small>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }}>
          <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
            Hızlı Erişim
          </div>
          <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ borderBottom: '1px solid #e9ecef', cursor: 'pointer' }}
              onClick={() => setPersonnelModalOpen(true)}
            >
              <div className="d-flex align-items-center">
                <i className="ri-team-line me-2 text-warning"></i>
                <span style={{ fontWeight: 500 }}>Personel</span>
              </div>
              <i className="ri-arrow-right-s-line text-muted"></i>
            </div>
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ borderBottom: '1px solid #e9ecef', cursor: 'pointer' }}
              onClick={() => setVehiclesModalOpen(true)}
            >
              <div className="d-flex align-items-center">
                <i className="ri-truck-line me-2 text-danger"></i>
                <span style={{ fontWeight: 500 }}>Araçlar</span>
              </div>
              <i className="ri-arrow-right-s-line text-muted"></i>
            </div>
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ borderBottom: '1px solid #e9ecef', cursor: 'pointer' }}
              onClick={() => setWorksModalOpen(true)}
            >
              <div className="d-flex align-items-center">
                <i className="ri-tools-line me-2 text-primary"></i>
                <span style={{ fontWeight: 500 }}>İşler</span>
              </div>
              <i className="ri-arrow-right-s-line text-muted"></i>
            </div>
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ borderBottom: '1px solid #e9ecef', cursor: 'pointer' }}
              onClick={() => setDailyRecordsModalOpen(true)}
            >
              <div className="d-flex align-items-center">
                <i className="ri-file-list-3-line me-2 text-info"></i>
                <span style={{ fontWeight: 500 }}>Günlük Kayıtlar</span>
              </div>
              <i className="ri-arrow-right-s-line text-muted"></i>
            </div>
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ cursor: 'pointer' }}
              onClick={() => setWaybillsModalOpen(true)}
            >
              <div className="d-flex align-items-center">
                <i className="ri-article-line me-2 text-success"></i>
                <span style={{ fontWeight: 500 }}>İrsaliyeler</span>
              </div>
              <i className="ri-arrow-right-s-line text-muted"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-modals */}
      <SitePersonnelModal
        isOpen={personnelModalOpen}
        toggle={() => setPersonnelModalOpen(false)}
        site={site ? { ...site, project: { name: site.project.name, city: { name: site.project.city.name } } } : null}
        onUpdate={onUpdate}
      />
      <SiteVehiclesModal
        isOpen={vehiclesModalOpen}
        toggle={() => setVehiclesModalOpen(false)}
        site={site ? { ...site, project: { name: site.project.name, city: { name: site.project.city.name } } } : null}
        projectId={site?.project.id || null}
        onUpdate={onUpdate}
      />
      <SiteWorksModal
        isOpen={worksModalOpen}
        toggle={() => setWorksModalOpen(false)}
        site={site ? { ...site, project: { name: site.project.name, city: { name: site.project.city.name } } } : null}
        onUpdate={onUpdate}
      />
      <SiteDailyRecordsModal
        isOpen={dailyRecordsModalOpen}
        toggle={() => setDailyRecordsModalOpen(false)}
        site={site ? { ...site, project: { name: site.project.name, city: { name: site.project.city.name } } } : null}
        onUpdate={onUpdate}
      />
      <SiteWaybillsModal
        isOpen={waybillsModalOpen}
        toggle={() => setWaybillsModalOpen(false)}
        site={site ? { ...site, project: { name: site.project.name, city: { name: site.project.city.name } } } : null}
        onUpdate={onUpdate}
      />
    </SideModal>
  );
};

export default SiteDetailModal;
